import React, { useCallback, useEffect, useState } from 'react';
import { readPolicySkill, createPolicySkill, readPolicyEnforcement } from '../../services/protectBuild';
import PolicySkillWizard from '../policy/PolicySkillWizard';
import { LABEL, NOTE } from './typeScale';
import {
  defaultPolicySkillName, defaultPolicySkillDescription, POLICY_SKILL_COPY,
} from '../../data/protectPolicySkill';

/**
 * What its protection is attributed to, created before any limits are set.
 *
 * <h3>Why the walk could not finish without this</h3>
 *
 * <p>An ExteLet's limits are materialized into enforceable rules, and the
 * backend attributes every rule it produces to the agent's one and only
 * `policy_skill` — `ExternalAgentPolicyBridge.resolveSolePolicySkillId:253`
 * looks for exactly one and returns null for none or several. Protect never
 * created one, so that returned null for every agent built here.</p>
 *
 * <p>The consequence is at `:308-320`: the pass that retires rules the tenant
 * has stopped asking for is skipped when there is no owner. Changing a limit
 * worked — those rules upsert by name — but <em>removing</em> one did not. It
 * kept enforcing, and nothing on this surface could clear it.</p>
 *
 * <p>It is also the object the Decide step reviews, which is the other half of
 * why Decide was a dead end for agents built here.</p>
 *
 * <h3>Why it comes before the measuring step</h3>
 *
 * <p>`PhaseGateService.promoteEvalReadyBuildSkills:348` only lifts a skill out
 * of build once it has a completed eval at or above the bar, and
 * `SkillReviewService:50` refuses to review a skill still in build. So this
 * policy has to exist while the measuring step is still ahead of it, or it sits
 * in build with nothing able to review it — the case that method's own javadoc
 * names: "an ExteLet's policy_skill stays in 'build' forever".</p>
 *
 * <h3>Why creating one is not finishing this step</h3>
 *
 * <p>A created policy skill is a shell. What gives it teeth is the platform's own configuration
 * pipeline — source selection, extraction, review, binding, activation — which turns documents,
 * pattern templates or framework controls into enforcement rules attributed to it. This step used
 * to create the shell and advance.</p>
 *
 * <p>That was not a cosmetic gap. A policy with no rules in force attributes nothing and refuses
 * nothing: every protected call passes a policy that says nothing about it, and the Decide review
 * — which exists so somebody reads what this agent may do before it acts — is handed an empty
 * object to approve. The walk was producing agents whose protection was a name.</p>
 *
 * <p><b>Correction (2026-11-03).</b> This paragraph used to argue from the eval score: that an
 * unconfigured policy scores 4 of 7 and that configuring it produced 7 of 7. That was a misreading
 * of a live 57%. The 4 of 7 is the config-completeness fallback scored when no judge runs, and the
 * 7 of 7 run was judged by the tenant's connected Anthropic connector — the judge was the
 * variable, not the configuration (docs/DESIGN_EVAL_JUDGE_ONE_OWNER.md). The step stands on the
 * ground above instead, which does not depend on how the skill is measured.</p>
 *
 * <p>So the pipeline is mounted here rather than reimplemented — it already supports being hosted
 * ({@code embeddedInSlideout}, {@code scopeAgentId}) and has its own tests. And the step advances
 * on evidence from the platform, at least one enabled rule sourced from this skill, never on the
 * pipeline merely reporting itself done: that optimism is what shipped the shell.</p>
 *
 * <h3>Why it refuses rather than warns</h3>
 *
 * <p>The gate this unblocks is two steps later. A step that advanced on a
 * failed create would turn its own cause into an unexplained refusal at the end
 * of the walk, so a refusal is shown here, where it can be acted on.</p>
 */
export default function PolicySkillAsk({
  agentId, agentName, onAnswer, busy = false,
}) {
  const [found, setFound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(() => defaultPolicySkillName(agentName));
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);

  const [enforcement, setEnforcement] = useState(null);
  /* Set only when the agent has more than one policy and the person has said which the limits
     attach to. The Build gate accepts two; attribution between them cannot be guessed, so it is
     asked rather than refused. */
  const [chosenId, setChosenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setChosenId(null);
    const res = await readPolicySkill(agentId);
    setFound(res);
    if (res?.skill?.id) setEnforcement(await readPolicyEnforcement(res.skill.id));
    setLoading(false);
  }, [agentId]);

  /* The policy this step is about: the agent's only one, or the one that was chosen. */
  const active = found?.skill
    || (chosenId ? (found?.policies || []).find((p) => p.id === chosenId) || null : null);

  const choose = async (policy) => {
    setChosenId(policy.id);
    setProblem(null);
    setEnforcement(null);
    setEnforcement(await readPolicyEnforcement(policy.id));
  };
  useEffect(() => { load(); }, [load]);

  const activeId = active?.id || null;
  const checkEnforcement = useCallback(async () => {
    if (!activeId) return null;
    setEnforcement(null);
    const next = await readPolicyEnforcement(activeId);
    setEnforcement(next);
    return next;
  }, [activeId]);

  const advance = (rules) => onAnswer({
    policyId: active.id, policyName: active.name, configured: true, rules,
  });

  /* The pipeline saying it is done is not the platform saying anything is in force. Only the
     re-read decides, or this advances on the same optimism that shipped the shell. */
  const pipelineFinished = async () => {
    setProblem(null);
    const next = await checkEnforcement();
    if (next && next.ok && next.enabled > 0) {
      onAnswer({
        policyId: active.id, policyName: active.name,
        configured: true, rules: next.enabled,
      });
      return;
    }
    if (next && next.ok) {
      setProblem('That finished, but this policy is not enforcing anything yet. Go back through '
                 + 'the steps and make sure at least one rule is selected and activated.');
    }
  };

  const create = async () => {
    const trimmed = name.trim();
    /* Checked here as well as in the service: the service is the contract, but
       asking the platform to reject something this screen already knows is
       wrong spends a round trip to say what it can say now. */
    if (!trimmed) {
      setProblem(POLICY_SKILL_COPY.nameRequired);
      return;
    }
    setWorking(true);
    setProblem(null);
    try {
      const res = await createPolicySkill(agentId, {
        name: trimmed,
        description: defaultPolicySkillDescription(agentName),
      });
      if (!res || res.ok === false || !res.skill?.id) {
        setProblem(res?.error || 'The platform would not create this protection policy.');
        return;
      }
      /* Created, not finished. A new policy enforces nothing, so reload into the configuration
         pipeline rather than advancing — advancing here was how the shell left this step. */
      await load();
    } catch (e) {
      setProblem(e?.message || 'Could not create this protection policy.');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return <p style={NOTE}>Looking for this agent’s protection policy…</p>;
  }

  /* A failed read is not an empty agent. Offering a create here could make a
     second policy on an agent that already has one, which leaves the backend
     with no single owner exactly as having none does. */
  if (found?.error) {
    return (
      <div style={COL}>
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{found.error}</p>
        <div>
          <button type="button" className="p7-btn" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  /* More than one policy is a state the Build gate accepts — it counts them and passes. This
     step used to refuse it and send the person away to delete one, which is both a harder
     instruction than the situation needs and a destructive one. What it actually needs is the
     one thing nobody can work out from the data: which policy these limits belong to. */
  if (found?.ambiguous && !active) {
    return (
      <div style={COL}>
        <div style={PANEL}>
          <p style={{ ...LABEL, margin: 0 }}>This agent has more than one protection policy</p>
          <p style={{ ...NOTE, margin: '4px 0 0' }}>
            Choose the one the limits you set next belong to. It is the policy reviewed before
            this agent may act, and the one its rules are attributed to.
          </p>
          <div style={{ marginTop: '9px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(found.policies || []).map((p) => (
              <button key={p.id} type="button" className="p7-btn" disabled={busy}
                      style={{ justifyContent: 'flex-start' }}
                      onClick={() => choose(p)}>
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (active) {
    if (enforcement === null) {
      return <p style={NOTE}>Checking what this protection policy enforces…</p>;
    }

    /* A refused read is not an unconfigured policy. This read needs Tenant Admin, so somebody
       below that role is told what happened rather than being held at a step they may already
       have finished, with the policy blamed for it. */
    if (!enforcement.ok) {
      return (
        <div style={COL}>
          <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>
            We could not check whether this protection policy is enforcing anything. Nothing has
            changed — try again, or open it in the full platform.
          </p>
          <div>
            <button type="button" className="p7-btn" onClick={checkEnforcement}>Try again</button>
          </div>
        </div>
      );
    }

    if (enforcement.enabled > 0) {
      return (
        <div style={COL}>
          <div style={PANEL}>
            <p style={{ ...LABEL, margin: 0 }}>{POLICY_SKILL_COPY.existsTitle}</p>
            <p style={{ ...NOTE, marginTop: '4px' }}>{active.name}</p>
            <p style={{ ...NOTE, marginTop: '6px' }}>
              {enforcement.enabled} rule{enforcement.enabled === 1 ? '' : 's'} in force, checked at
              the boundary on every protected call.
            </p>
          </div>
          <div>
            <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                    onClick={() => advance(enforcement.enabled)}>
              Continue
            </button>
          </div>
        </div>
      );
    }

    /* Created but enforcing nothing — the shell. The platform's own pipeline is mounted here so
       the person finishes it without leaving the walk. */
    return (
      <div style={COL}>
        <div style={PANEL}>
          <p style={{ ...LABEL, margin: 0 }}>{active.name}</p>
          <p style={{ ...NOTE, marginTop: '6px' }}>
            It exists but holds nothing back yet. Compile it from your policy documents, pattern
            templates or framework controls, and the rules it produces are enforced on every
            protected call.
          </p>
        </div>

        {problem ? (
          <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{problem}</p>
        ) : null}

        <PolicySkillWizard
          config={{ skillId: active.id }}
          scopeAgentId={agentId}
          embeddedInSlideout
          onComplete={pipelineFinished}
        />
      </div>
    );
  }

  return (
    <div style={COL}>
      <div style={PANEL}>
        <p style={{ ...NOTE, marginTop: 0 }}>{POLICY_SKILL_COPY.emptyBody}</p>
        <label htmlFor="ps-name" style={LABEL}>{POLICY_SKILL_COPY.nameLabel}</label>
        <input id="ps-name" className="p7-input" type="text" value={name}
               placeholder={POLICY_SKILL_COPY.namePlaceholder}
               onChange={(e) => setName(e.target.value)}
               style={{ display: 'block', marginTop: '4px', width: '100%', maxWidth: '420px' }} />
      </div>

      {problem ? (
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{problem}</p>
      ) : null}

      <div>
        <button type="button" className="p7-btn p7-btn--primary"
                disabled={busy || working || !agentId}
                onClick={create}>
          {working ? POLICY_SKILL_COPY.creating : POLICY_SKILL_COPY.createLabel}
        </button>
      </div>
    </div>
  );
}

const COL = { display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' };
const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '12px 13px', background: 'var(--p7-surface-sunken)',
};
